import React, { useState, useEffect } from 'react'
import { Button, Form } from 'semantic-ui-react'
import { useToasts } from 'react-toast-notifications'
import fbStatic from 'firebase/app'

import { useAuth } from 'lib/context/AuthContext'
import useFirebase from 'lib/hooks/useFirebase'
import { UseUserData } from 'lib/hooks/useUser'

import PhoneInput from './PhoneInput'

enum STEPS {
  EXISTS, // already added
  CTA_BUTTON, // no phone added
  PHONE_INPUT, // adding phone
  VERIFICATION_INPUT, // verification code input
}

const Exists = ({
  phoneNumber,
  onClick,
}: {
  phoneNumber: string
  onClick: any
}) => (
  <>
    <Form.Field
      control="input"
      value={phoneNumber}
      readOnly
      label="Phone Number"
      id="phoneNumber"
    />
    <Button children="Remove Phone" onClick={onClick} />
  </>
)

const CTAButton = ({ onClick }) => (
  <Form.Field>
    <label>Phone Number</label>
    <Button primary onClick={onClick}>
      Add your phone number
    </Button>
  </Form.Field>
)

const PhoneInputS = ({ user, onSuccess }) => {
  const [num, setNum] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [recaptcha, setRecaptcha] = useState({
    id: null,
    instance: null,
  })
  const recaptchaRef = React.useRef(null)

  useEffect(() => {
    const init = async () => {
      const re = new fbStatic.auth.RecaptchaVerifier(recaptchaRef.current, {
        size: 'invisible',
      })

      const recaptchaId = await re.render()

      setRecaptcha({
        id: recaptchaId,
        instance: re,
      })
    }

    if (!recaptchaRef.current || recaptcha.instance) {
      return
    }

    init()
  }, [recaptchaRef.current])

  const onChange = evt => setNum(evt.target.value)

  const link = async () => {
    setError(null)
    setLoading(true)
    const phoneNum = '+1' + num.replace(/\(|\)|\s|\-/g, '')

    try {
      const confirmationResult = await user.auth.linkWithPhoneNumber(
        phoneNum,
        recaptcha.instance,
      )
      onSuccess(confirmationResult)
      // onSuccess()
      return
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  return (
    <>
      <PhoneInput
        onChange={onChange}
        action={
          <Button
            color="teal"
            icon="plus"
            labelPosition="right"
            content="Add Number"
            onClick={link}
            loading={loading}
            disabled={loading}
          />
        }
        error={error}
      />
      <div ref={recaptchaRef} />
    </>
  )
}

const Verification = ({ confirmationResult, onSuccess }) => {
  const [num, setNum] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const onClick = async () => {
    setError(null)
    setLoading(true)
    try {
      const credential = await confirmationResult.confirm(num)
      onSuccess()
      return
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  return (
    <Form.Input
      type="text"
      pattern="\d*"
      label="Verification Code"
      value={num}
      maxLength={6}
      autoFocus
      onChange={evt => {
        setNum(evt.target.value)
      }}
      placeholder="Enter your verification code"
      action={{
        color: 'teal',
        icon: 'arrow right',
        labelPosition: 'right',
        content: 'Verify',
        onClick,
        loading,
        disabled: loading,
      }}
      error={error}
    />
  )
}

const PhoneVerification = ({ user }: { user: UseUserData }) => {
  const firebase = useFirebase()
  const [stage, setStage] = useState(
    user.auth.phoneNumber ? STEPS.EXISTS : STEPS.CTA_BUTTON,
  )
  const [confirmationResult, setConfirmationResult] = useState(null)
  const [, updateAuth] = useAuth()
  const { addToast } = useToasts()

  switch (stage) {
    case STEPS.EXISTS:
      return (
        <Exists
          phoneNumber={user.auth.phoneNumber}
          onClick={async () => {
            await firebase
              .auth()
              .currentUser.unlink(fbStatic.auth.PhoneAuthProvider.PROVIDER_ID)
            await updateAuth()
          }}
        />
      )
    case STEPS.CTA_BUTTON:
      return <CTAButton onClick={() => setStage(STEPS.PHONE_INPUT)} />
    case STEPS.PHONE_INPUT:
      return (
        <PhoneInputS
          user={user}
          onSuccess={cr => {
            addToast(
              'We sent your number a verification code. Please enter it next.',
              { appearance: 'info' },
            )
            setConfirmationResult(cr)
            setStage(STEPS.VERIFICATION_INPUT)
          }}
        />
      )
    case STEPS.VERIFICATION_INPUT:
      return (
        <Verification
          confirmationResult={confirmationResult}
          onSuccess={async () => {
            setStage(STEPS.EXISTS)
            await updateAuth()
          }}
        />
      )
    default:
      return null
  }
}

export default PhoneVerification
